/**
 * A conversa que a pessoa está olhando — D2 (passo 14, 07/10/2026), spec §7.
 *
 * A ChatPage publica; o avisador (`avisoDaMensagem`) e o contador do menu (`useNaoLidasDoChat`)
 * leem. Fora do React: o evento ao vivo chega no NotificationContext, que não sabe da ChatPage.
 *
 * "À vista" = esta sala E a aba visível — a conversa aberta numa aba escondida avisa: a pessoa
 * não está olhando. Quem publica `null` quando a conversa não está na tela (voltar à lista no
 * celular, aba "Atendimento externo", sair da página) é a ChatPage.
 *
 * ⚠️ `salaAbertaVisivel` é o instantâneo do `useSyncExternalStore`: devolve string ou `null`
 *    (comparáveis por valor), então é estável entre chamadas sem mudança. Devolver um objeto
 *    novo a cada chamada faria o React re-renderizar sem parar.
 */

/** Cada assinatura é uma entrada própria: o mesmo `ouvinte` assinado duas vezes sai por inteiro só nas duas. */
interface Assinatura {
  ouvinte: () => void;
}

let sala: string | null = null;
const assinaturas = new Set<Assinatura>();
let desligarVisibilidade: (() => void) | null = null;

function abaVisivel(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function avisarOuvintes(): void {
  // Cópia: um ouvinte que cancela a própria assinatura no meio da rodada não pula o seguinte.
  for (const { ouvinte } of [...assinaturas]) ouvinte();
}

/** Um `visibilitychange` só, enquanto houver quem escute (não um por componente). */
function ligarVisibilidade(): void {
  if (desligarVisibilidade || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", avisarOuvintes);
  desligarVisibilidade = () => document.removeEventListener("visibilitychange", avisarOuvintes);
}

function desligarSeNaoHaOuvintes(): void {
  if (assinaturas.size > 0 || !desligarVisibilidade) return;
  desligarVisibilidade();
  desligarVisibilidade = null;
}

/** A ChatPage diz qual conversa está aberta (`null` = nenhuma à vista). A mesma sala não avisa de novo. */
export function definirSalaAberta(id: string | null): void {
  if (id === sala) return;
  sala = id;
  avisarOuvintes();
}

/** A sala aberta, esteja a aba visível ou não. */
export function salaAberta(): string | null {
  return sala;
}

/** Esta sala está sendo olhada AGORA (aberta e aba visível)? Quem responde não avisa. */
export function salaAbertaAVista(roomId: string): boolean {
  return sala !== null && sala === roomId && abaVisivel();
}

/** A sala aberta com a aba visível, senão `null` — o instantâneo para o `useSyncExternalStore`. */
export function salaAbertaVisivel(): string | null {
  return sala !== null && abaVisivel() ? sala : null;
}

/**
 * Para o `useSyncExternalStore`: avisa quando a sala muda OU a aba aparece/esconde. O 1º ouvinte
 * liga o `visibilitychange` do `document`; o último a sair o desliga. Devolve o cancelamento.
 */
export function assinarSalaAberta(ouvinte: () => void): () => void {
  const assinatura: Assinatura = { ouvinte };
  assinaturas.add(assinatura);
  ligarVisibilidade();
  return () => {
    assinaturas.delete(assinatura);
    desligarSeNaoHaOuvintes();
  };
}

/** Só para os testes. */
export function _reiniciarSalaAbertaParaTeste(): void {
  sala = null;
  assinaturas.clear();
  desligarSeNaoHaOuvintes();
}
